import React, { useEffect, useState } from 'react';
import FormShell from './FormShell';
import { Button } from '../ui/button';
import { Input } from '../ui/input';
import { Label } from '../ui/label';
import { Card, CardContent, CardHeader, CardTitle } from '../ui/card';
import { useToast } from '../../hooks/use-toast';
import { loadSavedForm, saveForm } from './formUtils';

const emptyOwner = () => ({
  _uid: crypto.randomUUID(),
  fullName: '',
  idNumber: '',
  email: '',
  phone: '',
  holdingPercent: '',
  natureOfInterest: 'shareholder',
  residentialAddress: '',
});

const DEFAULTS = {
  companyName: '',
  registrationNumber: '',
  contactName: '',
  contactEmail: '',
  contactPhone: '',
  beneficialOwners: [emptyOwner()],
  notes: '',
};

const steps = ['Company', 'Beneficial Owners', 'Review'];

const BeneficialOwnershipForm = ({ serviceId, onComplete }) => {
  const { toast } = useToast();
  const [step, setStep] = useState(1);
  const [form, setForm] = useState(() => loadSavedForm(serviceId) || DEFAULTS);
  const [saved, setSaved] = useState(false);

  useEffect(() => {
    saveForm(serviceId, form);
    setSaved(true);
    const t = setTimeout(() => setSaved(false), 1500);
    return () => clearTimeout(t);
  }, [serviceId, form]);

  const set = (k, v) => setForm((p) => ({ ...p, [k]: v }));

  const totalHolding = useMemo(
    () =>
      form.beneficialOwners.reduce(
        (sum, o) => sum + (parseFloat(o.holdingPercent) || 0),
        0,
      ),
    [form.beneficialOwners],
  );
  const updateOwner = (i, field, value) =>
    setForm((p) => ({
      ...p,
      beneficialOwners: p.beneficialOwners.map((o, idx) =>
        idx === i ? { ...o, [field]: value } : o,
      ),
    }));
  const addOwner = () =>
    setForm((p) => ({ ...p, beneficialOwners: [...p.beneficialOwners, emptyOwner()] }));
  const removeOwner = (i) =>
    setForm((p) => ({
      ...p,
      beneficialOwners: p.beneficialOwners.filter((_, idx) => idx !== i),
    }));

  const validate = () => {
    if (step === 1 && (!form.companyName || !form.registrationNumber || !form.contactEmail)) {
      toast({
        title: 'Required Fields Missing',
        description: 'Company name, registration number and contact email are required.',
        variant: 'destructive',
      });
      return false;
    }
    if (step === 2) {
      const bad = form.beneficialOwners.some(
        (o) => !o.fullName || !o.idNumber || !o.holdingPercent,
      );
      if (bad) {
        toast({
          title: 'Required Fields Missing',
          description: 'Name, ID and % holding are required for every beneficial owner.',
          variant: 'destructive',
        });
        return false;
      }
      if (totalHolding > 100.0001) {
        toast({
          title: 'Invalid Holdings',
          description: `Total holding is ${totalHolding}% — must not exceed 100%.`,
          variant: 'destructive',
        });
        return false;
      }
    }
    return true;
  };

  const next = () => {
    if (!validate()) return;
    setStep((s) => Math.min(s + 1, steps.length));
    window.scrollTo(0, 0);
  };
  const prev = () => {
    setStep((s) => Math.max(s - 1, 1));
    window.scrollTo(0, 0);
  };
  const submit = () => {
    if (!validate()) return;
    onComplete(form);
  };

  return (
    <FormShell
      title="Beneficial Ownership Declaration"
      stepLabels={steps}
      currentStep={step}
      onNext={next}
      onPrevious={prev}
      onSubmit={submit}
      autoSaved={saved}
    >
      {step === 1 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-emerald-800">Company Information</h3>
          <div>
            <Label>Registered Company Name *</Label>
            <Input
              value={form.companyName}
              onChange={(e) => set('companyName', e.target.value)}
            />
          </div>
          <div>
            <Label>CIPC Registration Number *</Label>
            <Input
              value={form.registrationNumber}
              onChange={(e) => set('registrationNumber', e.target.value)}
            />
          </div>
          <div className="grid md:grid-cols-2 gap-3">
            <div>
              <Label>Contact Name</Label>
              <Input
                value={form.contactName}
                onChange={(e) => set('contactName', e.target.value)}
              />
            </div>
            <div>
              <Label>Contact Email *</Label>
              <Input
                type="email"
                value={form.contactEmail}
                onChange={(e) => set('contactEmail', e.target.value)}
              />
            </div>
          </div>
          <div>
            <Label>Contact Phone</Label>
            <Input
              value={form.contactPhone}
              onChange={(e) => set('contactPhone', e.target.value)}
            />
          </div>
        </div>
      )}

      {step === 2 && (
        <div className="space-y-4">
          <h3 className="text-xl font-semibold text-emerald-800">Beneficial Owners</h3>
          <p className="text-sm text-gray-600">
            Add everyone who ultimately owns / controls more than 5% of the company.
            Holdings must total ≤ 100%.
          </p>

          {form.beneficialOwners.map((o, i) => (
            <Card key={o._uid || `bo-${i}`}>
              <CardHeader className="bg-emerald-50">
                <div className="flex justify-between items-center">
                  <CardTitle className="text-lg">Beneficial Owner {i + 1}</CardTitle>
                  {form.beneficialOwners.length > 1 && (
                    <Button
                      variant="ghost"
                      size="sm"
                      onClick={() => removeOwner(i)}
                      className="text-red-600"
                    >
                      Remove
                    </Button>
                  )}
                </div>
              </CardHeader>
              <CardContent className="pt-4 space-y-3">
                <div>
                  <Label>Full Name *</Label>
                  <Input
                    value={o.fullName}
                    onChange={(e) => updateOwner(i, 'fullName', e.target.value)}
                  />
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <Label>ID / Passport *</Label>
                    <Input
                      value={o.idNumber}
                      onChange={(e) => updateOwner(i, 'idNumber', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Holding % *</Label>
                    <Input
                      type="number"
                      min="0"
                      max="100"
                      step="0.01"
                      value={o.holdingPercent}
                      onChange={(e) =>
                        updateOwner(i, 'holdingPercent', e.target.value)
                      }
                    />
                  </div>
                </div>
                <div className="grid md:grid-cols-2 gap-3">
                  <div>
                    <Label>Email</Label>
                    <Input
                      type="email"
                      value={o.email}
                      onChange={(e) => updateOwner(i, 'email', e.target.value)}
                    />
                  </div>
                  <div>
                    <Label>Phone</Label>
                    <Input
                      value={o.phone}
                      onChange={(e) => updateOwner(i, 'phone', e.target.value)}
                    />
                  </div>
                </div>
                <div>
                  <Label>Nature of Interest</Label>
                  <select
                    value={o.natureOfInterest}
                    onChange={(e) => updateOwner(i, 'natureOfInterest', e.target.value)}
                    className="w-full p-2 border border-gray-300 rounded-md"
                  >
                    <option value="shareholder">Shareholder</option>
                    <option value="controller">Controller / Board</option>
                    <option value="beneficiary">Beneficiary (trust)</option>
                    <option value="other">Other</option>
                  </select>
                </div>
                <div>
                  <Label>Residential Address</Label>
                  <Input
                    value={o.residentialAddress}
                    onChange={(e) =>
                      updateOwner(i, 'residentialAddress', e.target.value)
                    }
                  />
                </div>
              </CardContent>
            </Card>
          ))}

          <Button
            onClick={addOwner}
            variant="outline"
            className="w-full border-dashed border-2 border-emerald-300 text-emerald-600 hover:bg-emerald-50"
          >
            + Add Another Beneficial Owner
          </Button>
        </div>
      )}

      {step === 3 && (
        <Card className="bg-emerald-50 border-emerald-200">
          <CardHeader>
            <CardTitle className="text-emerald-800">Review</CardTitle>
          </CardHeader>
          <CardContent className="space-y-2 text-sm">
            <p><strong>Company:</strong> {form.companyName}</p>
            <p><strong>Reg No:</strong> {form.registrationNumber}</p>
            <p><strong>Contact:</strong> {form.contactName} — {form.contactEmail}</p>
            <p><strong>Beneficial Owners:</strong> {form.beneficialOwners.length}</p>
            <p>
              <strong>Total Holding:</strong> {totalHolding.toFixed(2)}%
            </p>
          </CardContent>
        </Card>
      )}
    </FormShell>
  );
};

export default BeneficialOwnershipForm;
